import { useLoading } from '@genial/components';
import {
  BasketCustomerOrderEntity,
  ErrorListResponse,
  instanceOfErrorListResponse,
} from '@genial/services';
import { ResponsesBasketCustomerOfferOrderResponse } from '@genial/services/dist/models';
import { useResetRecoilState, useSetRecoilState } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import {
  BatchTicketAssetsContextData,
  BatchTicketAssetsSelection,
} from '../../pages/BatchTicket/contexts';
import {
  cleanCustomersBasketService,
  deleteCustomerBasketService,
  postCustomerBasketService,
} from '../../pages/BatchTicket/services';
import { cleanProductsBasketService } from '../../pages/BatchTicket/services/products';
import {
  deleteRelateCustomerTag,
  patchCustomerTagService,
  patchDeleteTagService,
} from '../../pages/BatchTicket/services/tag';
import {
  BasketCustomerOrderItemResumeData,
  CartSectionStatus,
  CustomerItemDataProps,
  PostCustomerBasketServiceProps,
  RemoveCustomerOfferBasketParams,
  TagItemProp,
  Tags,
  UpdateCustomerOfferBasketParams,
  UpdateProductCartOfferParams,
} from '../../pages/BatchTicket/utils/types';
import { errorMessageBatchTicketRequestItem } from '../../pages/BatchTicketRequests/utils';

import { customerCartState, productsCartState, tagsState } from './atom';
import {
  addAndLinkTagSelector,
  addBasketCustomerOrderItemResumeSelector,
  addCustomerCartSelector,
  addCustomerListCartSelector,
  addProductsCartSelector,
  addProductsListCartSelector,
  linkUnlinkTagItemSelector,
  removeBasketCustomerOrderItemResumeSelector,
  removeCustomerCartSelector,
  removeCustomerOfferByBasketOrderResumeSelector,
  removeProductsCartSelector,
  removeTagSelector,
  updateBasketAdvisorOrderIdSelector,
  updateBasketCustomerOrderCoeOfferItemResumeSelector,
  updateBasketCustomerOrderItemResumeSelector,
  updateBatchTicketFiltersSelector,
  updateCustomerCartStatusSelector,
  updateCustomerOfferByBasketOrderResumeSelector,
  updateProductItemCartSelector,
  updateProductsCartStatusSelector,
  updateTagLabelSelector,
} from './selectors';

export const useBatchTicketActions = () => {
  const setAddAndLinkTagItem = useSetRecoilState(addAndLinkTagSelector);
  const setRemoveTagItem = useSetRecoilState(removeTagSelector);
  const setUpdateTagLabelItem = useSetRecoilState(updateTagLabelSelector);
  const setAddCustomerCartItem = useSetRecoilState(addCustomerCartSelector);
  const setAddCustomerListCart = useSetRecoilState(addCustomerListCartSelector);
  const setRemoveCustomerCartItem = useSetRecoilState(removeCustomerCartSelector);
  const setLinkUnlinkTagItem = useSetRecoilState(linkUnlinkTagItemSelector);
  const setBasketAdvisorOrderId = useSetRecoilState(updateBasketAdvisorOrderIdSelector);
  const setCustomerCartStatus = useSetRecoilState(updateCustomerCartStatusSelector);
  const setProductsCartStatus = useSetRecoilState(updateProductsCartStatusSelector);
  const setTags = useSetRecoilState(tagsState);
  const setBatchTicketCustomerFilters = useSetRecoilState(updateBatchTicketFiltersSelector);
  const setAddProductCartItem = useSetRecoilState(addProductsCartSelector);
  const setUpdateProductItemCart = useSetRecoilState(updateProductItemCartSelector);

  const setAddProductsListCart = useSetRecoilState(addProductsListCartSelector);
  const setRemoveProductCartItem = useSetRecoilState(removeProductsCartSelector);
  const setUpdateBasketCustomerOrderItem = useSetRecoilState(
    updateBasketCustomerOrderItemResumeSelector
  );
  const setUpdateBasketCustomerOrderCoeOfferItem = useSetRecoilState(
    updateBasketCustomerOrderCoeOfferItemResumeSelector
  );
  const resetProductsCart = useResetRecoilState(productsCartState);

  const setRemoveBasketCustomerOrderResumeItem = useSetRecoilState(
    removeBasketCustomerOrderItemResumeSelector
  );

  const setRemoveCustomerOfferByBasketOrderResumeItem = useSetRecoilState(
    removeCustomerOfferByBasketOrderResumeSelector
  );

  const setUpdateCustomerOfferItemByBasketOrderResume = useSetRecoilState(
    updateCustomerOfferByBasketOrderResumeSelector
  );

  const setBasketCustomerOrderResumeItem = useSetRecoilState(
    addBasketCustomerOrderItemResumeSelector
  );

  const { showToast, showToastFromError } = useToastCreator('toast-batch-ticket-basket-actions');
  const { hide, show } = useLoading();

  const resetCustomerCart = useResetRecoilState(customerCartState);

  const addTagsByAdvisor = (tags: Tags) => {
    setTags(tags);
    setBatchTicketCustomerFilters({} as never);
  };

  const addAndLinkTag = async (item: TagItemProp) => {
    const data = await patchCustomerTagService({
      customerId: item.customerId,
      tagCustomerItem: item,
    });

    if (data) {
      setAddAndLinkTagItem(item);
      setBatchTicketCustomerFilters({} as never);
    }
  };

  const linkUnlinkTag = async (item: TagItemProp) => {
    try {
      const data = await deleteRelateCustomerTag(item.tagId, item.customerId);

      if (data) {
        setLinkUnlinkTagItem(item);
        setBatchTicketCustomerFilters({} as never);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
      }
    }
  };

  const removeTag = async (id: string) => {
    try {
      const data = await patchDeleteTagService(id);
      if (data) {
        setRemoveTagItem(id);
        setBatchTicketCustomerFilters({} as never);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const updateTagLabel = async (item: TagItemProp) => {
    try {
      const data = await patchCustomerTagService({
        customerId: item.customerId,
        tagCustomerItem: item,
      });

      if (data) {
        setUpdateTagLabelItem(item);
        setBatchTicketCustomerFilters({} as never);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const addCustomerToCart = async ({
    basketAdvisorOrderId,
    customerItem,
  }: PostCustomerBasketServiceProps) => {
    try {
      const data = await postCustomerBasketService({ basketAdvisorOrderId, customerItem });

      if (data) {
        updateBasketAdvisorOrderId(data.basketCustomerOrder.basketAdvisorOrderId);
        setAddCustomerCartItem({
          ...customerItem,
          basketCustomerOrderId: data.basketCustomerOrder.basketCustomerOrderId,
        });
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const addCustomerListToCart = (data: CustomerItemDataProps[]) => {
    setAddCustomerListCart(data);
  };

  const addProductsListToCart = (data: BatchTicketAssetsSelection) => {
    setAddProductsListCart(data);
  };

  const removeCustomerToCart = async (customerItem: CustomerItemDataProps) => {
    try {
      const customerIsRemoved = await deleteCustomerBasketService(
        customerItem.basketCustomerOrderId
      );

      if (customerIsRemoved) {
        setRemoveCustomerCartItem(customerItem);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const addBasketCustomerOrderItemResume = (data: BasketCustomerOrderItemResumeData) => {
    setBasketCustomerOrderResumeItem(data);
  };

  const removeCustomerToBasketOrderResume = async (basketCustomerOrderId: string) => {
    try {
      const customerIsRemoved = await deleteCustomerBasketService(basketCustomerOrderId);

      if (customerIsRemoved) {
        setRemoveBasketCustomerOrderResumeItem(basketCustomerOrderId);
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const updateCustomerOfferInBasketOrderResume = (data: UpdateCustomerOfferBasketParams) => {
    setUpdateCustomerOfferItemByBasketOrderResume(data);
  };

  const cleanCustomersCart = async (basketAdvisorOrderId: string | null) => {
    try {
      if (basketAdvisorOrderId) {
        const customersBasketIsCleaned = await cleanCustomersBasketService(basketAdvisorOrderId);

        if (customersBasketIsCleaned) {
          resetCustomerCart();
        }
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    }
  };

  const onChangeCustomerCartStatus = (value: CartSectionStatus) => {
    setCustomerCartStatus(value);
  };

  const removeCustomerOfferByBasketOrderResume = (value: RemoveCustomerOfferBasketParams) => {
    setRemoveCustomerOfferByBasketOrderResumeItem(value);
  };

  const onChangeProductsCartStatus = (value: CartSectionStatus) => {
    setProductsCartStatus(value);
  };

  const addProductToCart: BatchTicketAssetsContextData['selectProduct'] = (
    productType,
    product
  ) => {
    setAddProductCartItem({ productType, product });
  };

  const removeProductToCart: BatchTicketAssetsContextData['deselectProduct'] = (
    productType,
    product,
    idSelector
  ) => {
    setRemoveProductCartItem({ productType, product, idSelector });
  };

  const cleanProductCart = async (basketAdvisorOrderId: string) => {
    try {
      show({
        title: 'Aguarde estamos limpando sua cesta.',
        description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
      });

      if (basketAdvisorOrderId) {
        const productsBasketIsCleaned = await cleanProductsBasketService(basketAdvisorOrderId);

        if (productsBasketIsCleaned) {
          resetProductsCart();
        }
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  const updateBasketAdvisorOrderId = (basketAdvisorOrderId: string) => {
    setBasketAdvisorOrderId(basketAdvisorOrderId);
  };

  const updateProductItemCart = (data: UpdateProductCartOfferParams) => {
    setUpdateProductItemCart(data);
  };

  const updateBasketCustomerOrderItem = (customerOrderItem: BasketCustomerOrderEntity) => {
    setUpdateBasketCustomerOrderItem(customerOrderItem);
  };

  const updateBasketCustomerCoeOrderItem = (
    customerOrderItem: ResponsesBasketCustomerOfferOrderResponse
  ) => {
    setUpdateBasketCustomerOrderCoeOfferItem(customerOrderItem);
  };

  return {
    addAndLinkTag,
    removeTag,
    linkUnlinkTag,
    updateTagLabel,
    addCustomerToCart,
    removeCustomerToCart,
    cleanCustomersCart,
    onChangeCustomerCartStatus,
    onChangeProductsCartStatus,
    addTagsByAdvisor,
    addProductToCart,
    removeProductToCart,
    cleanProductCart,
    addCustomerListToCart,
    updateBasketAdvisorOrderId,
    addProductsListToCart,
    removeCustomerToBasketOrderResume,
    addBasketCustomerOrderItemResume,
    removeCustomerOfferByBasketOrderResume,
    updateCustomerOfferInBasketOrderResume,
    updateBasketCustomerOrderItem,
    updateBasketCustomerCoeOrderItem,
    updateProductItemCart,
  };
};
